import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, EmptyState, Mono, PageBody, PageHeader } from "../ui/Field";

type Col = { id: string; key: string; name: string; name_plural: string; is_taxonomy: number; listing_path: string | null; created_at: number };

export default function Collections() {
  const { slug } = useParams();
  const [items, setItems] = useState<Col[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    api.get<{ items: Col[] }>(`/api/sites/${slug}/collections`).then((r) => setItems(r.items)).catch((e) => setErr((e as Error).message));
  }, [slug]);

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 03 · Collections"
        title={<>Content <em>types</em></>}
        description="Define typed content beyond pages: Posts, Products, Team, Events. Each gets its own schema, entries, and revisions."
        actions={<Link to={`/sites/${slug}/collections/new`} className="inline-flex h-9 items-center px-4 rounded-[var(--radius-sm)] bg-[var(--color-ink)] text-[var(--color-paper)] text-[13.5px] no-underline hover:bg-[var(--color-ink-2)] transition-colors">+ New collection</Link>}
      />
      {err && <Banner kind="error">{err}</Banner>}
      {items.length === 0 ? (
        <EmptyState
          title="No collections yet."
          description="Collections let you model structured content. A blog needs a Posts collection; a directory needs a Listings collection."
          action={<Link to={`/sites/${slug}/collections/new`} className="inline-flex h-9 items-center px-4 rounded-[var(--radius-sm)] bg-[var(--color-ink)] text-[var(--color-paper)] text-[13.5px] no-underline">Create one</Link>}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 reveal reveal-2">
          {items.map((c, i) => (
            <Link
              key={c.id}
              to={`/sites/${slug}/collections/${c.id}`}
              className="group bg-[var(--color-surface)] border border-[var(--color-rule)] rounded-[var(--radius-sm)] p-5 no-underline hover:border-[var(--color-ink)] transition-colors shadow-[var(--shadow-xs)]"
            >
              <div className="flex items-baseline justify-between gap-3">
                <Mono className="text-[11px]">{String(i + 1).padStart(2, "0")}</Mono>
                {c.is_taxonomy ? <Badge tone="accent">Taxonomy</Badge> : <Badge>Collection</Badge>}
              </div>
              <h3 className="display text-3xl text-[var(--color-ink)] mt-2 mb-1 group-hover:text-[var(--color-accent)] transition-colors">{c.name_plural}</h3>
              <Mono className="text-[12px]">{c.key}</Mono>
              {c.listing_path && <div className="text-[12px] text-[var(--color-muted)] mt-2">Listing: <Mono>{c.listing_path}</Mono></div>}
            </Link>
          ))}
        </div>
      )}
    </PageBody>
  );
}
